/**
 * ============================================================================
 * DATA LAYER: GESTIÓN DE ANÉCDOTAS (ADMIN / OVERLAYS)
 * ============================================================================
 */

const API_BASE = (process.env.REACT_APP_API_URL || '') + 'anecdotes';

/**
 * Obtiene una página de anécdotas desde la API con paginación por cursor.
 */
const GetAnecdotesPage = async ({ limit = 30, cursor = null, sort = 'DESC' } = {}) => {
    try {
        let url = `${API_BASE}/list?limit=${limit}&sort=${sort}`;
        if (cursor) url += `&cursor=${cursor}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return { success: false, message: errorData.message || 'Error al obtener anécdotas.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'No ha sido posible conectar con el servidor.' };
    }
};

/**
 * Obtiene todas las anécdotas sin límites de paginación (para exportación).
 */
const GetAllAnecdotes = async () => {
    try {
        const response = await fetch(`${API_BASE}/all`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return { success: false, message: errorData.message || 'Error al obtener todas las anécdotas.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'No ha sido posible conectar con el servidor.' };
    }
};

/**
 * Obtiene anécdotas nuevas que hayan llegado después de un ID específico.
 */
const GetNewerAnecdotes = async (lastId) => {
    try {
        const response = await fetch(`${API_BASE}/newer-than/${lastId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return { success: false, message: errorData.message || 'Error al buscar nuevas anécdotas.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de conexión al buscar nuevas anécdotas.' };
    }
};

/**
 * Alterna la visibilidad de una anécdota (Ocultar/Mostrar).
 */
const ToggleAnecdoteVisibility = async (id) => {
    try {
        const response = await fetch(`${API_BASE}/${id}/toggle-hide`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return { success: false, message: 'Error al cambiar visibilidad.' };
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de conexión.' };
    }
};

/**
 * Alterna el estado de "Visto/No Visto" manualmente.
 */
const ToggleAnecdoteShown = async (id) => {
    try {
        const response = await fetch(`${API_BASE}/${id}/toggle-shown`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return { success: false, message: 'Error al cambiar estado visto.' };
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de conexión.' };
    }
};

/**
 * Elimina una anécdota permanentemente.
 */
const DeleteAnecdote = async (id) => {
    try {
        const response = await fetch(`${API_BASE}/${id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return { success: false, message: 'Error al eliminar.' };
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de conexión.' };
    }
};

/**
 * Reinicia la dinámica completa (Borra todo).
 */
const ResetAnecdotes = async () => {
    try {
        const response = await fetch(`${API_BASE}/reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return { success: false, message: 'Error al reiniciar anécdotas.' };
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de conexión.' };
    }
};

/**
 * Proyecta una anécdota en el overlay del estadio con el modo especificado.
 */
const SelectAnecdote = async (id, mode = 'fullscreen') => {
    try {
        const response = await fetch(`${API_BASE}/${id}/select?mode=${encodeURIComponent(mode)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return { success: false, message: 'Error al proyectar anécdota.' };
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de conexión.' };
    }
};

/**
 * Quita la anécdota actual de la pantalla.
 */
const DeselectAnecdote = async () => {
    try {
        const response = await fetch(`${API_BASE}/clear`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return { success: false, message: 'Error al limpiar pantalla.' };
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de conexión.' };
    }
};

/**
 * Cambia en vivo el modo de visualización en el overlay.
 */
const SetAnecdoteMode = async (mode) => {
    try {
        const response = await fetch(`${API_BASE}/set-mode?mode=${encodeURIComponent(mode)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return { success: false, message: 'Error al cambiar modo de pantalla.' };
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de conexión.' };
    }
};

/**
 * Ajusta en vivo la posición vertical (offset Y) del cintillo en pantalla.
 */
const SetAnecdotePosition = async (offset) => {
    try {
        const response = await fetch(`${API_BASE}/position?offset=${offset}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return { success: false, message: 'Error al cambiar posición.' };
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de conexión.' };
    }
};

export {
    GetAnecdotesPage,
    GetAllAnecdotes,
    GetNewerAnecdotes,
    ToggleAnecdoteVisibility,
    ToggleAnecdoteShown,
    DeleteAnecdote,
    ResetAnecdotes,
    SelectAnecdote,
    DeselectAnecdote,
    SetAnecdoteMode,
    SetAnecdotePosition
};
